import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { User, Settings, LogOut, ChevronDown } from 'lucide-react';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import { getImageUrl } from '../../utils/imageUtils';
import './ProfileMenu.css';

const formatRoleLabel = (merchantRole) => {
  if (!merchantRole) return 'Team Member';
  return merchantRole
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

/**
 * Topbar profile chip + popover menu.
 *
 * Mobile-first: collapses to just the avatar on narrow viewports; name and
 * role badge appear at >=640px. Pulls user/store from auth + Redux directly,
 * not via props. Handles its own outside-click close and routes through
 * SecureAuthContext for logout (cookie-aware).
 */
const ProfileMenu = () => {
  const { user, logout: secureLogout } = useSecureAuth();
  const store = useSelector((s) => s.store?.current);

  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const onLogout = async () => {
    setOpen(false);
    await secureLogout();
  };

  const initial =
    store?.name?.charAt(0)?.toUpperCase() ||
    user?.email?.charAt(0)?.toUpperCase() ||
    'M';

  const roleLabel = user?.storeId ? formatRoleLabel(user?.merchantRole) : 'Store Owner';

  return (
    <div className="profile-menu" ref={wrapRef}>
      <button
        type="button"
        className={`profile-menu-trigger ${open ? 'open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Open profile menu"
      >
        <span
          className="profile-menu-avatar"
          style={
            store?.logo
              ? {
                  backgroundImage: `url(${getImageUrl(store.logo)})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }
              : undefined
          }
          aria-hidden="true"
        >
          {!store?.logo && initial}
        </span>
        <span className="profile-menu-body">
          <span className="profile-menu-name">{store?.name || 'Merchant'}</span>
          <span className="profile-menu-role">{roleLabel}</span>
        </span>
        <ChevronDown
          size={16}
          className={`profile-menu-chevron ${open ? 'open' : ''}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div className="profile-menu-popover" role="menu">
          <Link
            to="/profile"
            role="menuitem"
            className="profile-menu-item"
            onClick={() => setOpen(false)}
          >
            <User size={16} aria-hidden="true" />
            <span>Profile</span>
          </Link>
          <Link
            to="/settings"
            role="menuitem"
            className="profile-menu-item"
            onClick={() => setOpen(false)}
          >
            <Settings size={16} aria-hidden="true" />
            <span>Settings</span>
          </Link>
          <div className="profile-menu-divider" aria-hidden="true" />
          <button
            type="button"
            role="menuitem"
            className="profile-menu-item profile-menu-logout"
            onClick={onLogout}
          >
            <LogOut size={16} aria-hidden="true" />
            <span>Logout</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default ProfileMenu;
